import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { api, formatDate } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const NotificationItem = ({ n, onOpen }) => (
  <button onClick={() => onOpen(n)} data-testid={`notification-${n.notification_id}`}
    className={cn("flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-stone-50", !n.read && "bg-teal-50/50")}>
    <span className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", n.read ? "bg-transparent" : "bg-teal-600")} />
    <span className="min-w-0 flex-1">
      <span className="block text-sm font-semibold text-stone-900">{n.title}</span>
      <span className="block text-xs leading-relaxed text-stone-600">{n.body}</span>
      <span className="mt-0.5 block text-[11px] text-stone-400">{formatDate(n.created_at, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
    </span>
  </button>
);

export function useOpenNotification(after) {
  const navigate = useNavigate();
  return async (n) => {
    if (!n.read) await api.post(`/notifications/${n.notification_id}/read`).catch(() => {});
    after?.();
    navigate(n.link);
  };
}

export const NotificationBell = ({ user }) => {
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    const tick = () => api.get("/notifications/unread-count").then((r) => alive && setCount(r.data.count)).catch(() => {});
    tick();
    const id = setInterval(tick, 5000);
    return () => { alive = false; clearInterval(id); };
  }, [user.user_id]);

  const load = () => api.get("/notifications", { params: { limit: 8 } }).then((r) => setItems(r.data));
  const openN = useOpenNotification(() => { setOpen(false); setCount((c) => Math.max(0, c - 1)); });
  const readAll = async () => {
    await api.post("/notifications/read-all");
    setCount(0);
    load();
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button className="relative rounded-full p-2 text-stone-600 transition-colors hover:bg-stone-100 hover:text-stone-900" data-testid="notification-bell">
          <Bell className="h-5 w-5" />
          {count > 0 && (
            <span data-testid="notification-unread-count" className="absolute -right-0.5 -top-0.5 flex min-w-[1.1rem] items-center justify-center rounded-full bg-teal-700 px-1 text-[10px] font-bold text-white">{count}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 overflow-hidden rounded-2xl p-0" data-testid="notification-dropdown">
        <div className="flex items-center justify-between border-b border-stone-100 px-4 py-3">
          <p className="text-sm font-bold text-stone-900">Notifikasi</p>
          <button onClick={readAll} className="text-xs font-semibold text-teal-700 hover:underline" data-testid="notification-read-all">Tandai semua dibaca</button>
        </div>
        <div className="max-h-96 divide-y divide-stone-100 overflow-y-auto">
          {items.length === 0 && <p className="px-4 py-6 text-center text-sm text-stone-500">Belum ada notifikasi</p>}
          {items.map((n) => <NotificationItem key={n.notification_id} n={n} onOpen={openN} />)}
        </div>
        <Link to="/notifikasi" onClick={() => setOpen(false)} className="block border-t border-stone-100 py-3 text-center text-xs font-semibold text-teal-700 hover:bg-stone-50" data-testid="notification-see-all">
          Lihat semua notifikasi
        </Link>
      </PopoverContent>
    </Popover>
  );
};
